
const TRANSPARENT_GIF =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

export default {
  name: "OptimizedImage",

  props: {
    src: {
      type: String,
      required: true,
    },
    srcset: {
      type: String,
      default: "",
    },
    webpSrcset: {
      type: String,
      default: "",
    },
    avifSrcset: {
      type: String,
      default: "",
    },
    sizes: {
      type: String,
      required: true,
    },
    width: {
      type: [Number, String],
      required: true,
    },
    height: {
      type: [Number, String],
      required: true,
    },
    alt: {
      type: String,
      default: "",
    },
    loading: {
      type: String,
      default: "lazy",
      validator: (value) => ["lazy", "eager"].includes(value),
    },
    decoding: {
      type: String,
      default: "async",
      validator: (value) => ["async", "sync", "auto"].includes(value),
    },
    imgClass: {
      type: [String, Object, Array],
      default: "",
    },
    imgStyle: {
      type: [String, Object],
      default: null,
    },
    pictureClass: {
      type: [String, Object, Array],
      default: "",
    },
    disableAvif: {
      type: Boolean,
      default: false,
    },
    disableWebp: {
      type: Boolean,
      default: false,
    },
    vImg: {
      type: [Object, Boolean],
      default: false,
    },
    /**
     * Shorthand for marking this image as the LCP element of the page.
     * "high" sets loading=eager + decoding=sync + fetchpriority=high together,
     * overriding the individual `loading` and `decoding` props.
     * Use only for true LCP elements (e.g. profile avatar in header,
     * splash hero) — for everything else leave at "auto".
     */
    priority: {
      type: String,
      default: "auto",
      validator: (value) => ["high", "auto"].includes(value),
    },
  },

  data() {
    return {
      hasError: false,
      fallbackDisableAvif: false,
      fallbackDisableWebp: false,
    };
  },

  computed: {
    effectiveDisableAvif() {
      return this.disableAvif || this.fallbackDisableAvif;
    },

    effectiveDisableWebp() {
      return this.disableWebp || this.fallbackDisableWebp;
    },

    discreetMode() {
      return this.$store?.getters?.getDiscreetStatus || false;
    },

    /**
     * When priority="high", force eager loading regardless of the `loading` prop.
     */
    effectiveLoading() {
      return this.priority === "high" ? "eager" : this.loading;
    },

    /**
     * When priority="high", force synchronous decoding to avoid a frame
     * of empty space while the LCP image is being decoded.
     */
    effectiveDecoding() {
      return this.priority === "high" ? "sync" : this.decoding;
    },

    /**
     * Returns "high" only when explicitly requested. Returning null causes
     * Vue to omit the attribute entirely so the browser uses its default ("auto").
     */
    fetchpriorityAttr() {
      return this.priority === "high" ? "high" : null;
    },

    /**
     * Final src for the <img> tag. Replaced with a transparent placeholder
     * when discreet mode is on or when the picture has hard-failed.
     */
    displaySrc() {
      if (this.discreetMode || this.hasError) {
        return TRANSPARENT_GIF;
      }
      return this.src;
    },

    /**
     * Final srcset for the <img> tag (jpg fallback chain). Cleared in
     * discreet/error states so the browser does not try to fetch sized URLs.
     */
    displaySrcset() {
      if (this.discreetMode || this.hasError) return undefined;
      return this.srcset || undefined;
    },
  },

  watch: {
    src(newSrc, oldSrc) {
      if (newSrc !== oldSrc) {
        this.hasError = false;
        this.fallbackDisableAvif = false;
        this.fallbackDisableWebp = false;
      }
    },
  },

  methods: {
    handleError(event) {
      const failedSrc = event?.target?.currentSrc || event?.target?.src || "";

      // If AVIF failed, disable AVIF and let the browser try WebP
      if (failedSrc.includes(".avif") && !this.fallbackDisableAvif) {
        this.fallbackDisableAvif = true;
        return;
      }

      // If WebP failed, disable WebP and let the browser try the original format (JPG/PNG)
      if (failedSrc.includes(".webp") && !this.fallbackDisableWebp) {
        this.fallbackDisableWebp = true;
        return;
      }

      // If the original format also failed (or we don't know what failed), it's a hard error
      if (!this.hasError) {
        this.hasError = true;
      }
    },
  },
};
